import {useEffect, useState, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useCleanupOnUnload} from "../../hooks/useCleanupOnUnload";
import {
  requestDataExplorerAnalysis,
  requestDataExplorerTemplates,
  requestDataExplorerTimezones,
  storeDataExplorerAnalysis,
  storeDataExplorerTemplates,
  storeDataExplorerTimezones,
} from "../../redux/Actions/data-explorer.actions";
import {
  getDataExplorerAnalysis,
  getDataExplorerSensor,
  getDataExplorerTemplates,
  getDataExplorerTimezones,
} from "../../redux/Selectors/data-explorer.selectors";
import {
  getSelectedController,
  getInputSensorLabelValue,
} from "../../redux/Selectors/controllers.selectors";

export const useCleanup = () => {
  const dispatch = useDispatch();
  const cleanup = useCallback(() => {
    dispatch(storeDataExplorerTemplates([]));
    dispatch(storeDataExplorerTimezones([]));
    dispatch(storeDataExplorerAnalysis({}));
  }, [dispatch]);

  useCleanupOnUnload(cleanup);
};

export const useFetchAnalysis = () => {
  const dispatch = useDispatch();
  const sensor = useSelector(getDataExplorerSensor);

  const fetchAnalysis = (template, inputSensor, inputSensor2) => {
    if (template) {
      const payload = {
        sensor_id: sensor.id,
        method: template,
      };
      if (template === "dual_graph") {
        payload.input_id = inputSensor;
      }
      if (template === "triple_graph") {
        payload.input_ids = [inputSensor, inputSensor2];
      }
      dispatch(requestDataExplorerAnalysis(payload));
    }
  };

  return {fetchAnalysis};
};

export const useHandleForm = () => {
  const dispatch = useDispatch();
  const {fetchAnalysis} = useFetchAnalysis();

  const analysis = useSelector(getDataExplorerAnalysis);
  const inputSensorOptions = useSelector(getInputSensorLabelValue);
  const selectedController = useSelector(getSelectedController);
  const sensor = useSelector(getDataExplorerSensor);
  const templateOptions = useSelector(getDataExplorerTemplates);
  const timezoneOptions = useSelector(getDataExplorerTimezones);

  const filteredOptions = inputSensorOptions.filter(
    (option) => option.value !== sensor.id
  );

  const [inputSensor, setInputSensor] = useState(filteredOptions[0]?.value);
  const [inputSensor2, setInputSensor2] = useState(filteredOptions[1]?.value);
  const [template, setTemplate] = useState("");
  const [timezone, setTimezone] = useState("Local");

  const inputSensorOptions1 = filteredOptions.filter(
    (option) => option.value !== inputSensor2
  );
  const inputSensorOptions2 = filteredOptions.filter(
    (option) => option.value !== inputSensor
  );

  useEffect(() => {
    const templateCallback = (options) => {
      if (options?.length > 0) {
        const value = options[0].value;
        setTemplate(value);
        fetchAnalysis(value, inputSensor, inputSensor2);
      }
    };
    const timezoneCallback = (options) => {
      if (options?.length > 0) {
        setTimezone(options[0].value);
      }
    };
    dispatch(
      requestDataExplorerTemplates(
        {
          sensor_id: sensor.id,
        },
        templateCallback
      )
    );
    dispatch(
      requestDataExplorerTimezones(
        {
          mac: selectedController.mac,
        },
        timezoneCallback
      )
    );
    return () => {
      setTemplate("");
      setTimezone("Local");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = (value) => {
    setTemplate(value);
    fetchAnalysis(value, inputSensor, inputSensor2);
  };

  const handleInputSensorChange = (value) => {
    setInputSensor(value);
    fetchAnalysis(template, value, inputSensor2);
  };

  const handleInputSensor2Change = (value) => {
    setInputSensor2(value);
    fetchAnalysis(template, inputSensor, value);
  };

  return {
    analysis,
    handleTemplateChange,
    handleInputSensorChange,
    handleInputSensor2Change,
    inputSensor,
    inputSensor2,
    inputSensorOptions: inputSensorOptions1,
    inputSensorOptions2,
    setTimezone,
    template,
    templateOptions,
    timezone,
    timezoneOptions,
  };
};
